import type { FrontUnit } from '../maps/frontsMath'

/**
 * Кадр серии: что показывает одна картинка архива.
 *
 * Серия отвечает на вопрос, который одним кадром не закрыть: где рыба и почему
 * там. Промысловик собирал этот набор руками — переключал раздел, выставлял
 * изотерму, фронты, изобаты, волны и жал PNG, — а параметры сводил в блокноте.
 *
 * СОСТАВ кадров с 07.09.2026 считается из каталога слоёв (`seriesCompose.ts`), а
 * не лежит здесь списком: владелец попросил «абсолютно все картинки», а список
 * руками протух бы на первом же новом слое и протух бы молча. Здесь остались
 * ФОРМА кадра и то немногое, что каталог знать не может, — порог изотермы
 * рабочего кадра по виду.
 */

/**
 * Слой кадра — тот же идентификатор, что у каталога слоёв бэкенда
 * (`src/cabinet/layer_catalog.py`). Кадр серии показывается ПОДМЕНОЙ РАСТРА, а
 * не переключением раздела: раздел на «Моделях» тянет за собой три асинхронных
 * эффекта, которые перезапустились бы посреди серии и затёрли кадр своим слоем.
 */
export type SeriesLayer = string

export interface SeriesFrame {
  /** Код кадра: идёт в имя файла внутри архива и в parametry.txt. Латиница —
   *  имена в архиве не зависят от языка кабинета.
   *
   *  С 07.09.2026 это КОД СЛОЯ либо КОД ОВЕРЛЕЯ, а не выдуманное имя: серия
   *  собирается из каталога, и второй словарь имён разъехался бы с ним молча.
   *  Прежние коды `drift`/`hsi` были именно такими выдумками. */
  id: string
  /** Что показывает кадр: поле слоя или один оверлей поверх подложки. */
  kind: 'layer' | 'overlay'
  /** Оверлеи, включённые НА ЭТОМ кадре. У кадра слоя пусто: чужие линии поверх
   *  поля мешают читать то, ради чего кадр и снят. */
  overlays: string[]
  layer: SeriesLayer
  /** Ключ каталога переводов для названия кадра. Подписи здесь нет — только ключ. */
  labelKey: string
  /** Глубина кадра; null — слой глубины не знает (апвеллинг, батиметрия). */
  depth: number | null
  /** Градус изотермы; null — инструмент на кадре выключен. */
  isotherm: number | null
  /** Градус поверхностной изотермы (0 м); null — выключена. */
  isothermSurface: number | null
  /** Шаг смежных изотерм, °C; null — смежные выключены. */
  isothermSpread: number | null
  /** Порог термофронта в выбранной единице; null — выключен. */
  fronts: number | null
  frontsUnit: FrontUnit
  /** Шаг изобат, м; null — выключены. */
  isobathStep: number | null
  /** Шаг изолиний волнения, м; null — оверлей волнения выключен. Одно поле, а
   *  не пара «включено + шаг»: два способа сказать «выключено» однажды
   *  разойдутся. */
  waveStep: number | null
  /** «Траектории по HSI» — читаются только поверх поля течений. */
  hsiDrift: boolean
}

/** Глубина рабочего слоя серии (владелец 2026-08-18). */
export const SERIES_DEPTH = 20

/** Инструменты выключены все до одного. Отдельная константа, потому что
 *  «выключено» у всех кадров, кроме рабочего, обязано быть одним и тем же
 *  значением: иначе настройки первого кадра однажды протекут в остальные. */
export const NO_TOOLS = {
  isotherm: null,
  isothermSurface: null,
  isothermSpread: null,
  fronts: null,
  frontsUnit: '100km' as FrontUnit,
  isobathStep: null,
  waveStep: null,
  hsiDrift: false,
}

/**
 * Порог изотермы РАБОЧЕГО кадра по виду.
 *
 * Каталог слоёв этого знать не может: число выбирает владелец под промысел, и
 * оно и есть тот порог, по которому читают карту сноса. Вид, которого здесь
 * нет, получает кадр течений без инструментов — выдуманная изотерма подписала
 * бы кадр числом, которого никто не выбирал, и пережила бы верификацию.
 */
export const SERIES_ISOTHERM: Record<number, number> = {
  // сельдь (Clupea pallasii)
  4: 7.5,
  // сайра
  26: 9.5,
}
